import { useRoutes, Navigate, Outlet } from 'react-router-dom';
import { lazy, Suspense, useEffect, useState } from 'react';
import { ErrorBoundary } from '@/shared/components/ErrorBoundary';
import { AuthGuard, LoggedOutGuard } from '@host/guards';
import AppLayout from '@host/components/AppLayout';
import { LoggedOutSupportShell } from '@host/layout/LoggedOutSupportShell';
import { GuestAuthGuard } from '../guards/GuestAuthGuard';
import { useAuthStore } from '@host/stores/auth.store';

// Lazy-loaded page components for code splitting
const WelcomePage = lazy(() => import('@host/welcome/pages/WelcomePage'));
const AuditoriumJoinPage = lazy(() => import('@/apps/auditorium/pages/AuditoriumJoinPage'));
const DirectoryLandingPage = lazy(() => import('@host/welcome/pages/DirectoryLandingPage'));
const PrivacyPolicyPage = lazy(() => import('@host/welcome/pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('@host/welcome/pages/TermsOfServicePage'));
const WorkplacePage = lazy(() =>
  import('@host/workplace/pages/WorkplacePage').then((m) => ({ default: m.WorkplacePage }))
);
const WorkplaceLayout = lazy(() =>
  import('../workplace/layout/WorkplaceLayout').then((m) => ({ default: m.WorkplaceLayout }))
);
const DevBench = lazy(() => import('@host/devbench/DevBench'));
const SharedRecording = lazy(() =>
  import('@/apps/recording/components/Recordings/SharedRecording').then((m) => ({
    default: m.SharedRecording,
  }))
);
const GuestLayout = lazy(() =>
  import('@host/guest/layout/GuestLayout').then((m) => ({ default: m.GuestLayout }))
);
const GuestWorkplaceLayout = lazy(() =>
  import('@host/guest/layout/GuestWorkplaceLayout').then((m) => ({
    default: m.GuestWorkplaceLayout,
  }))
);
const GuestLobbyPage = lazy(() =>
  import('@host/guest/pages/GuestLobbyPage').then((m) => ({ default: m.GuestLobbyPage }))
);
const GuestWorkplacePage = lazy(() =>
  import('@host/guest/pages/GuestWorkplacePage').then((m) => ({ default: m.GuestWorkplacePage }))
);
const GuestInvitePage = lazy(() =>
  import('@host/guest/pages/GuestInvitePage').then((m) => ({ default: m.GuestInvitePage }))
);
const JoinOrgPage = lazy(() =>
  import('@host/welcome/pages/JoinOrgPage').then((m) => ({ default: m.JoinOrgPage }))
);
const WebexIntegrationRedirectHandler = lazy(() =>
  import('@/apps/webexMeet/handlers/WebexIntegrationRedirectHandler').then((m) => ({
    default: m.WebexIntegrationRedirectHandler,
  }))
);
const CXIntegrationAuthRedirectHandler = lazy(() =>
  import('@/apps/office/views/nexgenCX/handlers/CXIntegrationAuthRedirectHandler').then((m) => ({
    default: m.CXIntegrationAuthRedirectHandler,
  }))
);
const OAuthCallbackHandler = lazy(() =>
  import('@host/components/OAuthCallbackHandler').then((m) => ({ default: m.OAuthCallbackHandler }))
);
const MicrosoftIntegrationRedirectHandler = lazy(() =>
  import('@/apps/calendar/handlers/MicrosoftIntegrationRedirectHandler').then((m) => ({
    default: m.MicrosoftIntegrationRedirectHandler,
  }))
);
const CalendarConnectorRedirectHandler = lazy(() =>
  import('@/apps/administration/handlers/CalendarConnectorRedirectHandler').then((m) => ({
    default: m.CalendarConnectorRedirectHandler,
  }))
);
const ParkPage = lazy(() =>
  import('../workplace/pages/ParkPage').then((m) => ({ default: m.ParkPage }))
);
const ResetPassword = lazy(() =>
  import('../workplace/pages/ResetPassword').then((m) => ({ default: m.ResetPassword }))
);
const SignupPage = lazy(() => import('@host/signup/pages/SignupPage'));
const SignupPaymentReturnPage = lazy(() =>
  import('@host/signup/pages/SignupPaymentReturnPage').then((m) => ({
    default: m.SignupPaymentReturnPage,
  }))
);
import { isCurrentUserGuest } from '@/shared/utils/guest-detection.util';

const ProtectedLayout = () => (
  <AuthGuard>
    <Outlet />
  </AuthGuard>
);

const ConditionalRedirect: React.FC = () => {
  const { getToken } = useAuthStore.getState();
  const [checking, setChecking] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isGuest, setIsGuest] = useState(false);
  const [orgSlug, setOrgSlug] = useState<string | null>(null);

  useEffect(() => {
    const verify = async () => {
      try {
        const storedOrgSlug = localStorage.getItem('org-slug');
        setOrgSlug(storedOrgSlug);

        // Check if user is a guest
        const guest = isCurrentUserGuest();
        setIsGuest(guest);

        if (guest) {
          // For guests, only check if token exists (don't check expiration)
          // The API interceptor will handle token refresh automatically
          const guestToken = localStorage.getItem('isUserGuest');
          setIsAuthenticated(!!guestToken);
        } else {
          // For normal users, check token existence
          // Cognito handles token refresh automatically
          const token = await getToken();
          setIsAuthenticated(!!token);
        }
      } catch (error) {
        console.error('Error checking authentication:', error);
        setIsAuthenticated(false);
      } finally {
        setChecking(false);
      }
    };

    verify();
  }, [getToken]);

  if (checking) {
    return <div>Loading...</div>;
  }

  // Guest users must be redirected to guest-workplace, NOT workplace.
  // Workplace is guarded by AuthGuard which requires Cognito tokens that guests don't have.
  if (isAuthenticated && orgSlug && isGuest) {
    return <Navigate to={`/${orgSlug}/guest-workplace`} replace />;
  }

  if (isAuthenticated && orgSlug) {
    return <Navigate to={`/${orgSlug}/workplace`} replace />;
  }

  if (isAuthenticated) {
    return <Navigate to='/welcome' replace />;
  }

  if (orgSlug) {
    return <Navigate to={`/${orgSlug}/welcome`} replace />;
  }

  if (!isAuthenticated && !orgSlug) {
    return <Navigate to='/welcome' replace />;
  }

  return <Navigate to='/welcome' replace />;
};

const routes = [
  {
    path: '/recording/:recordingId',
    element: <SharedRecording />,
  },
  {
    path: '/',
    element: <AppLayout />,
    children: [
      { index: true, element: <ConditionalRedirect /> },
      {
        path: ':orgSlug',
        children: [
          {
            path: '',
            element: <ProtectedLayout />,
            children: [
              {
                path: '',
                element: <Navigate to='workplace' />,
              },
              {
                path: 'reset-password',
                element: <ResetPassword />,
              },
              {
                path: 'workplace',
                element: <WorkplaceLayout />,
                children: [{ index: true, element: <WorkplacePage /> }],
              },
            ],
          },
        ],
      },
      {
        path: 'welcome',
        element: (
          <LoggedOutGuard>
            <DirectoryLandingPage />
          </LoggedOutGuard>
        ),
      },
      {
        path: 'privacy-policy',
        element: (
          <LoggedOutGuard>
            <PrivacyPolicyPage />
          </LoggedOutGuard>
        ),
      },
      {
        path: 'terms-of-service',
        element: (
          <LoggedOutGuard>
            <TermsOfServicePage />
          </LoggedOutGuard>
        ),
      },
      {
        path: 'signup',
        element: (
          <LoggedOutGuard>
            <SignupPage />
          </LoggedOutGuard>
        ),
      },
      {
        path: 'signup/payment-return',
        element: (
          <LoggedOutSupportShell>
            <SignupPaymentReturnPage />
          </LoggedOutSupportShell>
        ),
      },
      {
        path: ':orgSlug/cross-org-invite/accept',
        element: <JoinOrgPage />,
      },
      {
        // NGE-1078: the one auditorium join link. Unguarded on purpose — the page
        // resolves the token publicly, then sends a signed-in member straight in and
        // anyone else through guest capture.
        path: ':orgSlug/auditorium/join/:token',
        element: <AuditoriumJoinPage />,
      },
      {
        path: 'meeting/join/:token',
        element: <GuestInvitePage />,
      },
      {
        path: ':orgSlug/meeting/join/:token',
        element: <GuestInvitePage />,
      },
      {
        path: ':orgSlug/lobby',
        element: (
          <GuestAuthGuard>
            <GuestLayout />
          </GuestAuthGuard>
        ),
        children: [{ index: true, element: <GuestLobbyPage /> }],
      },
      {
        path: ':orgSlug/guest-workplace',
        element: (
          <GuestAuthGuard>
            <GuestWorkplaceLayout />
          </GuestAuthGuard>
        ),
        children: [{ index: true, element: <GuestWorkplacePage /> }],
      },
      {
        path: ':orgSlug/welcome',
        element: (
          <LoggedOutGuard>
            <WelcomePage />
          </LoggedOutGuard>
        ),
      },
      {
        // NGE-681: auditorium guest entry — same welcome guest login, with the
        // session in the URL so "Here to See?" is the auditorium moderator and the
        // guest is auto-moved into the auditorium after the form.
        path: ':orgSlug/welcome/:sessionPid/join',
        element: (
          <LoggedOutGuard>
            <WelcomePage />
          </LoggedOutGuard>
        ),
      },
      {
        path: ':orgSlug/login',
        element: (
          <LoggedOutGuard>
            <WelcomePage />
          </LoggedOutGuard>
        ),
      },
      {
        path: ':orgSlug/forgot-password',
        element: (
          <LoggedOutGuard>
            <WelcomePage />
          </LoggedOutGuard>
        ),
      },
      {
        path: ':orgSlug/park',
        element: (
          <LoggedOutGuard>
            <ParkPage />
          </LoggedOutGuard>
        ),
      },
      {
        path: 'dev/*',
        element: (
          <LoggedOutGuard>
            <DevBench />
          </LoggedOutGuard>
        ),
      },
      { path: 'dev/apps/:appslug/views/:viewslug', element: <DevBench /> },
      { path: 'dev/apps/:appslug/settings/:viewslug', element: <DevBench /> },
      {
        path: 'sso/webex/complete',
        element: <WebexIntegrationRedirectHandler />,
      },
      {
        path: 'sso/cx/:integration/complete',
        element: <CXIntegrationAuthRedirectHandler />,
      },
      {
        path: 'auth/callback',
        element: <OAuthCallbackHandler />,
      },
      {
        path: 'sso/microsoft/complete',
        element: <MicrosoftIntegrationRedirectHandler />,
      },
      {
        path: 'sso/calendar-connector/complete',
        element: <CalendarConnectorRedirectHandler />,
      },
      {
        path: '*',
        element: <ConditionalRedirect />,
      },
    ],
  },
];

export const AppRoutes: React.FC = () => {
  return (
    <ErrorBoundary>
      <Suspense fallback={<div />}>{useRoutes(routes)}</Suspense>
    </ErrorBoundary>
  );
};
